import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Image,
  Input,
  Modal,
  message,
  Pagination,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import { DownloadOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons'
import axios from 'axios'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import { appApiUrls } from '@shared/url-map.js'
import type { TimelineImageAdminItem, TimelineImageAdminListResponse } from '@shared/common-types.js'
import OverviewCard from './OverviewCard.js'

const { RangePicker } = DatePicker
const { Text, Paragraph } = Typography

type TimelineStatusFilter = 'all' | 'pending' | 'success' | 'failed' | 'text-only'
type DateRangeValue = [Dayjs | null, Dayjs | null] | null

const STATUS_META: Record<TimelineStatusFilter, { label: string; color: string }> = {
  all: { label: '全部', color: 'default' },
  pending: { label: '排队中', color: 'default' },
  success: { label: '已完成', color: 'green' },
  failed: { label: '失败', color: 'red' },
  'text-only': { label: '仅文字', color: 'orange' },
}

export default function TimelineImagesPage() {
  const [records, setRecords] = useState<TimelineImageAdminItem[]>([])
  const [loading, setLoading] = useState(false)
  const [statusFilter, setStatusFilter] = useState<TimelineStatusFilter>('all')
  const [dateRange, setDateRange] = useState<DateRangeValue>(null)
  const [keyword, setKeyword] = useState('')
  const [keywordInput, setKeywordInput] = useState('')
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 12,
    total: 0,
    totalPages: 0,
  })
  const [detailRecord, setDetailRecord] = useState<TimelineImageAdminItem | null>(null)
  const [textPreview, setTextPreview] = useState<{ title: string; content: string } | null>(null)

  const fetchTimelineImages = useCallback(
    async (
      page = 1,
      pageSize = pagination.pageSize,
      overrides?: { keyword?: string; status?: TimelineStatusFilter; range?: DateRangeValue },
    ) => {
      setLoading(true)
      try {
        const effectiveKeyword = overrides?.keyword ?? keyword
        const effectiveStatus = overrides?.status ?? statusFilter
        const effectiveRange = overrides?.range ?? dateRange

        const params: Record<string, string | number> = {
          page,
          pageSize,
        }

        if (effectiveStatus !== 'all') {
          params.status = effectiveStatus
        }

        if (effectiveKeyword) {
          params.keyword = effectiveKeyword
        }

        if (effectiveRange?.[0] && effectiveRange?.[1]) {
          params.dateFrom = effectiveRange[0].startOf('day').toISOString()
          params.dateTo = effectiveRange[1].endOf('day').toISOString()
        }

        const response = await axios.get<{ code: number; data: TimelineImageAdminListResponse | null }>(
          appApiUrls.timelineImage.list,
          { params },
        )

        if (!response.data?.data) {
          throw new Error('响应数据为空')
        }

        const payload = response.data.data
        setRecords(payload.list)
        setPagination(payload.pagination)
      } catch (error) {
        console.error('获取时间线图片失败:', error)
        message.error('获取时间线图片失败，请稍后重试')
      } finally {
        setLoading(false)
      }
    },
    [dateRange, keyword, pagination.pageSize, statusFilter],
  )

  useEffect(() => {
    fetchTimelineImages(1, pagination.pageSize)
  }, [fetchTimelineImages, pagination.pageSize])

  const onSearch = (value: string) => {
    const trimmed = value.trim()
    setKeyword(trimmed)
    setKeywordInput(trimmed)
    fetchTimelineImages(1, pagination.pageSize, { keyword: trimmed })
  }

  const handleDateChange = (value: DateRangeValue) => {
    if (value && value[0] && value[1]) {
      setDateRange([value[0], value[1]])
    } else {
      setDateRange(null)
    }
  }

  const handleStatusChange = (value: TimelineStatusFilter) => {
    setStatusFilter(value)
  }

  const handleTextClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, title: string, content?: string | null) => {
      const target = event.target as HTMLElement
      if (!content || target.closest('.ant-typography-copy')) {
        return
      }
      setTextPreview({ title, content })
    },
    [],
  )

  const handleSelectUser = (userToken: string) => {
    onSearch(userToken)
    document.getElementById('timeline-images-filters')?.scrollIntoView({ behavior: 'smooth' })
  }

  const handlePaginationChange = (page: number, pageSize?: number) => {
    fetchTimelineImages(page, pageSize ?? pagination.pageSize)
  }

  const statusOptions = useMemo(
    () =>
      (Object.keys(STATUS_META) as TimelineStatusFilter[]).map((key) => ({
        label: STATUS_META[key].label,
        value: key,
      })),
    [],
  )

  return (
    <div className="space-y-6 p-6">
      <OverviewCard onSelectUser={handleSelectUser} />

      <Card id="timeline-images-filters">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <Space wrap>
            <Select value={statusFilter} options={statusOptions} style={{ width: 150 }} onChange={handleStatusChange} />
            <RangePicker
              allowClear
              value={dateRange ?? undefined}
              onChange={(value) => handleDateChange((value as DateRangeValue) ?? null)}
            />
            <Input.Search
              placeholder="搜索 userToken / IP / 日期 / 图片ID"
              allowClear
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onSearch={onSearch}
              style={{ width: 280 }}
            />
          </Space>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => fetchTimelineImages(pagination.current, pagination.pageSize, { keyword })}
          >
            刷新
          </Button>
        </div>
      </Card>

      <Card>
        {records.length === 0 && !loading ? (
          <Empty description="暂无数据" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {records.map((record) => {
              const statusMeta =
                STATUS_META[(record.statusLabel as TimelineStatusFilter) || 'pending'] ?? STATUS_META.pending

              return (
                <div
                  key={record.id}
                  className="flex h-[460px] flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="relative h-48 w-full bg-gray-50">
                    {record.imageUrl ? (
                      <Image
                        src={record.thumbUrl ?? record.imageUrl}
                        alt={record.imageId}
                        className="h-full w-full object-cover"
                        height="100%"
                        width="100%"
                        loading="lazy"
                        preview={{
                          src: record.imageUrl,
                          maskClassName: 'flex items-center justify-center',
                          mask: (
                            <div className="flex items-center gap-2">
                              <EyeOutlined />
                              预览
                            </div>
                          ),
                        }}
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-sm text-gray-400">暂无图片</div>
                    )}
                    <Tag
                      color={statusMeta.color === 'default' ? undefined : statusMeta.color}
                      className="absolute left-3 top-3 rounded-full px-3 py-1 text-xs"
                    >
                      {statusMeta.label}
                    </Tag>
                  </div>

                  <div className="flex flex-1 flex-col gap-3 p-4">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{dayjs(record.createdAt).format('MM-DD HH:mm')}</span>
                      <span>{record.modelDuration ? `${record.modelDuration.toLocaleString()} ms` : '—'}</span>
                    </div>
                    <div>
                      <Text className="text-base font-semibold text-gray-900">{record.date}</Text>
                      <Paragraph
                        className="mb-0 cursor-pointer text-xs text-gray-500"
                        ellipsis={{ rows: 2 }}
                        style={{ whiteSpace: 'pre-wrap' }}
                        onClick={(event) => handleTextClick(event, 'Activities', record.activities)}
                      >
                        {record.activities}
                      </Paragraph>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3 text-xs text-gray-500">
                      <div className="font-medium text-gray-800">{record.userToken || '未留Token'}</div>
                      <div>{record.userIp || '未知IP'}</div>
                      <div>
                        {(record.appInfo?.appVersion || 'Unknown') + ' · ' + (record.appInfo?.osVersion || 'Unknown')}
                      </div>
                      <div>Locale: {record.localizationCode || '--'}</div>
                    </div>
                    <div className="flex flex-1 flex-col gap-2 text-xs text-gray-600">
                      <div>
                        <Text type="secondary">Prompt</Text>
                        <Paragraph
                          className="mb-0 cursor-pointer text-xs"
                          ellipsis={{ rows: 2 }}
                          style={{ whiteSpace: 'pre-wrap' }}
                          onClick={(event) => handleTextClick(event, 'Prompt', record.prompt)}
                        >
                          {record.prompt}
                        </Paragraph>
                      </div>
                      {record.responseContent && (
                        <div>
                          <Text type="secondary">AI 响应</Text>
                          <Paragraph className="mb-0 text-xs" ellipsis={{ rows: 2, tooltip: record.responseContent }}>
                            {record.responseContent}
                          </Paragraph>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-xs text-gray-500">
                    <div>ID: {record.imageId || '--'}</div>
                    <Space>
                      <Tooltip title="查看详情">
                        <Button size="small" onClick={() => setDetailRecord(record)}>
                          详情
                        </Button>
                      </Tooltip>
                      {record.imageUrl && (
                        <Tooltip title="下载图片">
                          <Button
                            size="small"
                            type="default"
                            icon={<DownloadOutlined />}
                            href={appApiUrls.timelineImage.file(record.imageId)}
                            target="_blank"
                          >
                            下载
                          </Button>
                        </Tooltip>
                      )}
                    </Space>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div className="mt-6 flex justify-end">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            showTotal={(total) => `共 ${total} 条`}
            onChange={handlePaginationChange}
            onShowSizeChange={handlePaginationChange}
          />
        </div>
      </Card>

      {detailRecord && (
        <Drawer key={detailRecord.id} title="时间线图片详情" open width={720} onClose={() => setDetailRecord(null)}>
          <div className="space-y-4">
            {detailRecord.imageUrl ? (
              <Image
                src={detailRecord.imageUrl}
                alt={detailRecord.imageId}
                className="max-h-96 w-full rounded-md object-cover"
              />
            ) : (
              <Tag color="warning">当前记录没有生成图片</Tag>
            )}
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="日期">{detailRecord.date}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={STATUS_META[(detailRecord.statusLabel as TimelineStatusFilter) || 'pending']?.color}>
                  {STATUS_META[(detailRecord.statusLabel as TimelineStatusFilter) || 'pending']?.label}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="图片 ID">
                <Text copyable>{detailRecord.imageId}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="模型">
                {detailRecord.modelName} · {detailRecord.modelDuration || '--'} ms
              </Descriptions.Item>
              <Descriptions.Item label="Locale">{detailRecord.localizationCode || '--'}</Descriptions.Item>
              <Descriptions.Item label="用户">
                <div className="space-y-1 text-sm text-gray-600">
                  <div>Token：{detailRecord.userToken || '未留'}</div>
                  <div>IP：{detailRecord.userIp}</div>
                  <div>
                    App：{detailRecord.appInfo?.appVersion || '--'} · {detailRecord.appInfo?.osVersion || '--'}
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {dayjs(detailRecord.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
            </Descriptions>

            <div>
              <Text type="secondary">Activities</Text>
              <Paragraph copyable>{detailRecord.activities}</Paragraph>
            </div>

            <div>
              <Text type="secondary">Prompt</Text>
              <Paragraph copyable className="text-xs leading-6">
                {detailRecord.prompt}
              </Paragraph>
            </div>

            {detailRecord.responseContent && (
              <div>
                <Text type="secondary">AI 响应</Text>
                <Paragraph copyable className="text-xs leading-6">
                  {detailRecord.responseContent}
                </Paragraph>
              </div>
            )}

            {detailRecord.errorMessage && <Tag color="red">错误：{detailRecord.errorMessage}</Tag>}
          </div>
        </Drawer>
      )}
      {textPreview && (
        <Modal open title={textPreview.title} footer={null} width={680} onCancel={() => setTextPreview(null)}>
          <Paragraph className="text-sm leading-6" copyable style={{ whiteSpace: 'pre-wrap' }}>
            {textPreview.content}
          </Paragraph>
        </Modal>
      )}
    </div>
  )
}
